import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Row,
  Col,
} from "reactstrap";
import { LoadingOutlined, } from "@ant-design/icons";
import { Flex, Spin } from "antd/lib";
import { Block, Icon, Button, DataTable } from "../../../components/Component";
import { TableColumnsType, Table, Empty } from "antd";
import { useForm, FormProvider } from "react-hook-form";
import Swal from "sweetalert2";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import { nanoid } from "nanoid";
import Content from "../../../layout/content/Content";
import FormCheckbox from "../../form-components/CheckboxComponent";
export interface Question {
  id: string;
  name: string;
  type: string;
  options?: string;
  mandatory: boolean;
  description?: string;
}
interface QuestionerProps {
  initialQuestions?: Question[];
  onQuestionsUpdate?: any
  showHeading?: boolean;
  isLoading?: boolean;
}

const ChecklistCreation: React.FC<QuestionerProps> = ({
  initialQuestions = [],
  onQuestionsUpdate,
  isLoading = false,
}) => {
  const formMethods = useForm({ mode: "onChange" });
  const { handleSubmit, reset, watch } = formMethods;
  const [questions, setQuestions] = React.useState<Question[]>(initialQuestions);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });
  const [isAddQuestionModalOpen, setIsAddQuestionModalOpen] = useState(false);
  const questionType = watch("type");

  useEffect(() => {
    onQuestionsUpdate?.(questions);
  }, [questions, onQuestionsUpdate]);

  // Auto-dismiss alerts after 2.5 seconds
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({ type: "", message: "", errors: [] });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  // Handles adding a new question
  const onAddQuestion = useCallback((data: any) => {
    // Create new question object
    const newId = nanoid(); //every new item has a unique id
    const questionToAdd: Question = {
      id: "question-" + newId,
      name: data.name,
      type: data.type,
      options: data.type === "Text" ? "" : data.options,
      mandatory: Boolean(data.mandatory),
      description: data.description,
    };
    setQuestions(prev => [...prev, questionToAdd]);
    reset();
    setIsAddQuestionModalOpen(false);
    Swal.fire({
      icon: "success",
      title: "Question Added Successfully",
      text: "Your question has been saved.",
      confirmButtonText: "OK",
    });
  }, [questions, reset]);

  const handleDeleteQuestion = useCallback((id: string) => {
    Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "You won't to delete this!",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        setQuestions(prev => prev.filter((q) => q.id != id));
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Your question has been deleted.",
          confirmButtonText: "OK",
        }).then(() => {
          setIsAddQuestionModalOpen(false);
        });
      }
    });
  }, []);

  // Toggles add question 
  const toggleAddQuestionModal = useCallback(() => {
    setIsAddQuestionModalOpen(prev => !prev);
    if (!isAddQuestionModalOpen) {
      reset();
    }
  }, [isAddQuestionModalOpen, reset]);


  const columns: TableColumnsType<Question> = useMemo(() => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 400,
      fixed: "left",
      render: (text, record) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text}
          {record.mandatory && <span className="text-danger ml-1">*</span>}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type) => {
        switch (type.toLowerCase()) {
          case "text":
            return "Text";
          case "single_choice":
            return "Single Selection";
          case "multiple_choice":
            return "Multiple Selection";
          default:
            return type;
        };
      },
    },
    {
      title: "Options",
      dataIndex: "options",
      key: "options",
      width: 300,
      render: (text, record) => (
        <div className="truncate" style={{ maxWidth: "300px" }}>
          {(record.type) === "text" ? "N/A" : text}
        </div>
      ),
    },
    {
      title: "Mandatory",
      dataIndex: "mandatory",
      key: "mandatory",
      width: 100,
      render: (mandatory) => (mandatory ? "Yes" : "No"),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          size="sm"
          onClick={() => handleDeleteQuestion(record.id)}
        >
          <Icon name="trash"></Icon>
        </Button>
      ),
    },
  ], [handleDeleteQuestion]);

  //  table data
  const tableData = useMemo(() =>
    questions.map((q) => ({
      ...q,
      key: q.id.toString(),
      options: q.options || "",
    })),
    [questions]
  );

  if (isLoading) {
    return (
      <Flex align="center" gap="middle" className="pageLoader">
        <Spin
          indicator={<LoadingOutlined spin />}
          className="m-auto"
          size="large"
        />
      </Flex>
    );
  }

  return (
    <Content className="p-0">
      <Block className="border-none p-0 m-0">
        <FormProvider {...formMethods}>
          {alertInfo.message && (
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="mt-2"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={alertInfo.type === "error" ? "alert-circle" : "check-circle"}
              />
            </Alert>
          )}
          <div className="flex justify-end mx-5 mb-2">
            <Button
              className="btn btn-primary btn-md"
              type="button"
              color="primary"
              size="sm"
              onClick={toggleAddQuestionModal}
            >
              <Icon name="plus" />
              <span> Add Question </span>
            </Button>
          </div>
          {/* Main content */}
          <div className="mt-0">
            {questions.length > 0 && (
              <div className="flex justify-between items-center mx-7 mb-2 border-none">
                <h5 className="text-lg font-extrabold tracking-wide">
                  Questionnaire :
                </h5>
              </div>
            )}

            {/*table */}
            <div className="g-blue-50 rounded mx-7" style={{ border: 'none' }}>
              {questions.length > 0 ? (
                <div className="p-0">
                  <Table
                    columns={columns}
                    dataSource={tableData}
                    rowKey="key"
                    bordered={false}
                    expandable={{
                      expandedRowRender: (record) => (
                        <div className="px-4 py-2 text-gray-700">
                          <div className="mt-1">
                            {record.description
                              ? record.description
                              : "No description"}
                          </div>
                        </div>
                      ),
                      rowExpandable: () => true,
                      defaultExpandAllRows: false,
                    }}
                    pagination={false}
                    scroll={{ x: true }}
                    className="w-full border-none"
                    style={{ border: 'none' }}
                  />
                </div>
              ) : (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  backgroundColor: "white",
                }}>
                  <Empty description="No Data Found " />
                </div>
              )}
            </div>
          </div>

          {/* Add Modal */}
          <Modal
            isOpen={isAddQuestionModalOpen}
            size="lg"
            className="font-medium text-gray-900 modal-dialog-centered border-none"
            style={{ maxWidth: "1000px", width: "100%", border: 'none' }}
            key="addQuestionModal"
            contentClassName="border-none"
          >
            <ModalHeader toggle={toggleAddQuestionModal} className="border-0">
              <h4>Add New Question</h4>
            </ModalHeader>
            <ModalBody
              className="text-gray-900 p-4 border-none"
              style={{ minHeight: "350px" }}
            >
              <form
                className="content clearfix"
                onSubmit={handleSubmit(onAddQuestion)}
              >
                <div className="row g-3">
                  <div className="col-6">
                    <div className="d-flex flex-column">
                      <FormInput
                        name="name"
                        label="Name"
                        required={true}
                        type="text"
                        placeholder="Enter Question Name"
                      />
                      <div className="mt-1" style={{ marginLeft: "-25px" }}>
                        <FormCheckbox
                          name="mandatory"
                          optionLabel="Mandatory"
                          required={false}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-6">
                    <FormSelect
                      name="type"
                      label="Type"
                      required={true}
                      placeholder="Select Question Type"
                      options={[
                        { value: "Text", label: "Text" },
                        { value: "single_choice", label: "Single Selection" },
                        { value: "multiple_choice", label: "Multiple Selection" },
                      ]}
                    />
                  </div>
                  <div className="col-12">
                    {["single_choice", "multiple_choice"].includes(questionType) && (
                      <FormInput
                        name="options"
                        label="Options"
                        type="text"
                        placeholder="Enter options separated by commas (e.g., Dry, Wet, Moist)"
                        required={true}
                      />
                    )}
                  </div>
                  <div className="col-12">
                    <FormTextArea
                      name="description"
                      label="Description"
                      placeholder="Enter Description"
                    />
                  </div>
                </div>
                <Row className="mt-4">
                  <Col lg="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <a
                          href="#cancel"
                          onClick={toggleAddQuestionModal}
                          className="link link-light"
                        >
                          Cancel
                        </a>
                      </li>
                      <li>
                        <Button
                          className="btn btn-primary btn-md"
                          type="submit"
                          color="primary"
                        >
                          Save
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </form>
            </ModalBody>
          </Modal>
        </FormProvider>
      </Block>
    </Content>
  );
};

export default React.memo(ChecklistCreation);